import { ApiError } from "@/lib/time/api-client";
import { MAX_ENTRY_HOURS, MIN_ENTRY_HOURS } from "@/lib/time/constants";
import { parseHoursText } from "@/lib/time/log-time";
import { formatHours, isQuarterHour } from "@/lib/time/rules";
import type { CalendarMeetingView } from "@/schemas/calendar";
import type { AssignableTask } from "@/schemas/time";

/** What became of a meeting on this page: still to do, logged by Accept, or dismissed. */
type RowState = { kind: "pending" } | { kind: "logged" } | { kind: "dismissed" };

/** One meeting in the "From your calendar" panel with the task and hours the person has chosen. */
export interface PanelRow {
  meeting: CalendarMeetingView;
  chosen: AssignableTask | null;
  /** The hours box as typed; starts at the meeting's length. */
  hours: string;
  state: RowState;
}

/** A new row for a meeting the server returned: its suggestion and its length. */
export function freshRow(m: CalendarMeetingView): PanelRow {
  return {
    meeting: m,
    chosen: m.suggestion,
    hours: formatHours(m.hours),
    state: { kind: "pending" },
  };
}

/** The row's typed hours when Accept can log them (0.25 to 24, quarter hours), else null. */
export function rowHours(row: PanelRow): number | null {
  const hours = parseHoursText(row.hours);
  if (hours === null || Number.isNaN(hours)) return null;
  if (hours < MIN_ENTRY_HOURS || hours > MAX_ENTRY_HOURS || !isQuarterHour(hours)) return null;
  return hours;
}

/** What is wrong with the row's typed hours, or null when Accept can log them. */
export function hoursProblem(row: PanelRow): string | null {
  if (rowHours(row) !== null) return null;
  const hours = parseHoursText(row.hours);
  if (hours === null) return "Enter hours";
  if (Number.isNaN(hours) || hours < MIN_ENTRY_HOURS || hours > MAX_ENTRY_HOURS) {
    return "Hours must be 0.25 to 24";
  }
  return "Use quarter hours";
}

/** The chosen task differs from the suggestion (another task, or the same task on another assignment). */
export function isChanged(row: PanelRow): boolean {
  const suggested = row.meeting.suggestion;
  const chosen = row.chosen;
  if (!chosen) return false;
  if (!suggested) return true;
  return (
    chosen.projectTaskId.toLowerCase() !== suggested.projectTaskId.toLowerCase() ||
    chosen.demandPersonId.toLowerCase() !== suggested.demandPersonId.toLowerCase()
  );
}

/** The rows "Accept N matched" logs: pending, with a task and valid hours, in display order. */
export function acceptable(rows: PanelRow[]): PanelRow[] {
  return rows.filter(
    (r) => r.state.kind === "pending" && r.chosen !== null && rowHours(r) !== null,
  );
}

/** The header's "N meetings · H hrs": pending meetings only, at the hours typed (else their length). */
export function panelSummary(rows: PanelRow[]): { count: number; hours: number } {
  const pending = rows.filter((r) => r.state.kind === "pending");
  const hours = pending.reduce((sum, r) => sum + (rowHours(r) ?? r.meeting.hours), 0);
  return { count: pending.length, hours: Math.round(hours * 100) / 100 };
}

/** "Weekly sync, Tue 13 10:00 AM": the row context in every control's accessible name. */
export function meetingName(m: CalendarMeetingView): string {
  return `${m.title}, ${m.dayLabel} ${m.timeLabel}`;
}

const CLOCK = /^(\d{1,2}):(\d{2})\s*([AP])\.?\s*M\.?$/i;

/** Minutes after midnight of a "10:00 AM" label, or null when it is not one. */
function minutesOf(label: string): number | null {
  const m = CLOCK.exec(label.trim());
  if (!m) return null;
  const hour = Number(m[1]) % 12;
  const pm = (m[3] ?? "").toUpperCase() === "P";
  return (hour + (pm ? 12 : 0)) * 60 + Number(m[2]);
}

/**
 * The server's pending meetings plus the ones handled on this page (it never returns those
 * again), by date then start time. A row kept here (a task picked, logged, dismissed) replaces
 * the server's copy of its meeting.
 * Within a date the server's order (by start) stands whenever a time cannot be read.
 */
export function mergeHandled(
  server: CalendarMeetingView[],
  local: Map<string, PanelRow>,
): PanelRow[] {
  const fromServer = server.map((m): PanelRow => local.get(m.key) ?? freshRow(m));
  const onServer = new Set(server.map((m) => m.key));
  // A pending row the server no longer returns (handled elsewhere) is not shown again.
  const localOnly = [...local.values()].filter(
    (r) => !onServer.has(r.meeting.key) && r.state.kind !== "pending",
  );
  return [...fromServer, ...localOnly].sort((a, b) => {
    if (a.meeting.date !== b.meeting.date) return a.meeting.date < b.meeting.date ? -1 : 1;
    const ta = minutesOf(a.meeting.timeLabel);
    const tb = minutesOf(b.meeting.timeLabel);
    return ta !== null && tb !== null ? ta - tb : 0;
  });
}

/**
 * The entry id Accept sends for a meeting: made once and reused for every retry on this page,
 * so a retry after the entry saved (say a 500 from the step after it) is answered "Already
 * saved" instead of logging the hours twice (plan decision 11).
 */
export function entryIdFor(ids: Map<string, string>, eventKey: string, make: () => string): string {
  const existing = ids.get(eventKey);
  if (existing) return existing;
  const id = make();
  ids.set(eventKey, id);
  return id;
}

/** "1 hr" / "1.5 hrs": a meeting's length wherever the panel shows it. */
export function hoursText(hours: number): string {
  const text = formatHours(hours);
  return `${text} ${text === "1" ? "hr" : "hrs"}`;
}

/**
 * Undo answered "Nothing to undo": an earlier Undo landed (its answer was lost, or it ran in
 * another tab), so the entry is already gone and the meeting can be logged again.
 */
export function alreadyUndone(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

/**
 * An Accept failed in a way that may still have saved the entry (a 5xx, a 504 "may have
 * saved", or no answer at all). A 4xx is a plain refusal: nothing was written.
 */
export function mayHaveSaved(err: unknown): boolean {
  return !(err instanceof ApiError) || err.status >= 500;
}
